import React, { useState, useEffect } from 'react';
import { Mail, Save, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useNavigation } from '../contexts/NavigationContext';
import { APP_NAME } from '../constants/app';

interface EmailPreferences {
  emailCategories: {
    generic?: boolean;
    feature_education?: boolean;
    usage_achievement?: boolean;
    underutilization?: boolean;
    upgrade_prompt?: boolean;
  };
  unsubscribedAll: boolean;
}

export const EmailPreferenceCenter: React.FC = () => {
  const { t } = useTranslation();
  const { currentPath } = useNavigation();
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [accountName, setAccountName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Extract token from URL
  useEffect(() => {
    const pathSegments = currentPath.split('/');
    const tokenIndex = pathSegments.indexOf('email-preferences');
    if (tokenIndex >= 0 && pathSegments[tokenIndex + 1]) {
      const token = pathSegments[tokenIndex + 1];
      fetchPreferences(token);
    } else {
      setError('Invalid preference center link');
      setLoading(false);
    }
  }, [currentPath]);

  const fetchPreferences = async (token: string) => {
    try {
      const response = await fetch(`${apiUrl}/api/edm/preferences/${token}`);
      const data = await response.json();
      if (data.success && data.data.preferences) {
        setPreferences(data.data.preferences);
        setUserEmail(data.data.userEmail || '');
        setAccountName(data.data.accountName || '');
      } else {
        setError('Invalid or expired preference center link');
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setError('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!preferences) return;

    const pathSegments = currentPath.split('/');
    const tokenIndex = pathSegments.indexOf('email-preferences');
    if (tokenIndex < 0 || !pathSegments[tokenIndex + 1]) {
      setError('Invalid token');
      return;
    }

    const token = pathSegments[tokenIndex + 1];

    try {
      setSaving(true);
      setError(null);
      const response = await fetch(`${apiUrl}/api/edm/preferences/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emailCategories: preferences.emailCategories,
          unsubscribedAll: preferences.unsubscribedAll
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(data.message || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setError('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCategory = (category: keyof EmailPreferences['emailCategories']) => {
    if (!preferences) return;
    
    setPreferences({
      ...preferences,
      emailCategories: {
        ...preferences.emailCategories,
        [category]: !preferences.emailCategories[category]
      },
      unsubscribedAll: false
    });
  };

  const handleUnsubscribeAll = () => {
    if (!preferences) return;
    
    setPreferences({
      ...preferences,
      unsubscribedAll: !preferences.unsubscribedAll,
      emailCategories: preferences.unsubscribedAll ? {
        generic: true,
        feature_education: true,
        usage_achievement: true,
        underutilization: true,
        upgrade_prompt: true
      } : {
        generic: false,
        feature_education: false,
        usage_achievement: false,
        underutilization: false,
        upgrade_prompt: false
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="text-gray-500 dark:text-gray-400 mb-2">{t('common.loading') || 'Loading...'}</div>
        </div>
      </div>
    );
  }

  if (error && !preferences) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full m-4">
          <div className="text-center">
            <div className="mb-6">
              <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
            </div>
            <XCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('edm.preferenceCenter.error') || 'Error'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!preferences) {
    return null;
  }

  const categories = [
    { key: 'generic' as const, label: t('edm.preferences.categoryGeneric') || 'Generic Marketing' },
    { key: 'feature_education' as const, label: t('edm.preferences.categoryFeatureEducation') || 'Feature Education' },
    { key: 'usage_achievement' as const, label: t('edm.preferences.categoryUsageAchievement') || 'Usage Achievements' },
    { key: 'underutilization' as const, label: t('edm.preferences.categoryUnderutilization') || 'Underutilization Alerts' },
    { key: 'upgrade_prompt' as const, label: t('edm.preferences.categoryUpgradePrompt') || 'Upgrade Prompts' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mb-6">
              <img src="/logo.svg" alt={APP_NAME} className="w-20 h-20 mx-auto object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('edm.preferenceCenter.title') || 'Email Preferences'}
            </h1>
            {userEmail && (
              <p className="text-gray-600 dark:text-gray-400">
                {userEmail}
                {accountName && ` • ${accountName}`}
              </p>
            )}
          </div>

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border border-green-200 dark:border-green-800 rounded-lg flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
              <span className="text-success-800 dark:text-success-200">
                {t('edm.preferenceCenter.saved') || 'Your preferences have been saved successfully!'}
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-2">
              <XCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
              <span className="text-error-800 dark:text-error-200">{error}</span>
            </div>
          )}

          {/* Unsubscribe All */}
          <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.unsubscribedAll}
                onChange={handleUnsubscribeAll}
                className="mt-1 w-5 h-5 text-accent-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-900 dark:text-white block">
                  {t('edm.preferences.unsubscribeAll') || 'Unsubscribe from all marketing emails'}
                </span>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t('edm.preferences.unsubscribeAllDescription') || 'You will stop receiving all marketing emails, but may still receive important account notifications'}
                </p>
              </div>
            </label>
          </div>

          {/* Category Preferences */}
          {!preferences.unsubscribedAll && (
            <div className="space-y-4 mb-6">
              <h3 className="font-medium text-gray-900 dark:text-white">
                {t('edm.preferences.categoryPreferences') || 'Email Categories'}
              </h3>
              {categories.map(category => (
                <label
                  key={category.key}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{category.label}</span>
                  <input
                    type="checkbox"
                    checked={preferences.emailCategories[category.key] || false}
                    onChange={() => handleToggleCategory(category.key)}
                    className="w-5 h-5 text-accent-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </label>
              ))}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 font-medium"
            >
              <Save className="w-5 h-5" />
              <span>{saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save Preferences')}</span>
            </button>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>
              {t('edm.preferenceCenter.footer') || 'You can update these preferences at any time. Changes take effect immediately.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
